import React from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaTooth, FaGem, FaTools, FaComments } from "react-icons/fa";

const ServicesSection = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: false });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const services = [
    {
      id: 1,
      icon: <FaTooth />,
      title: "Custom Grillz",
      description: "Premium custom-fitted grillz made with the finest materials.",
    },
    {
      id: 2,
      icon: <FaGem />,
      title: "Custom Jewelry",
      description: "Bespoke jewelry pieces handcrafted to your specifications.",
    },
    {
      id: 3,
      icon: <FaTools />,
      title: "Restoration",
      description: "Expert restoration and repair services for your pieces.",
    },
    {
      id: 4,
      icon: <FaComments />,
      title: "Consultations",
      description: "Expert guidance for custom piece design and creation.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <SectionContainer id="services" ref={ref}>
      <BackgroundGlow />

      <Container>
        <SectionHeader>
          <motion.span
            className="subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            What We Offer
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.2 },
              },
            }}
          >
            Our Services
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.4 },
              },
            }}
          >
            From custom grillz to bespoke jewelry, we offer premium services
            crafted with exceptional attention to detail.
          </motion.p>
        </SectionHeader>

        <ServicesGrid
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {services.map((service) => (
            <ServiceCard key={service.id} variants={itemVariants}>
              <IconWrapper>
                {service.icon}
              </IconWrapper>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
            </ServiceCard>
          ))}
        </ServicesGrid>

        <ButtonContainer
          initial={{ opacity: 0, y: 30 }}
          animate={controls}
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.6, delay: 0.8 },
            },
          }}
        >
          <ViewAllButton
            to="/services"
            whileHover={{
              y: -5,
              boxShadow: "0 10px 25px rgba(255, 215, 0, 0.3)",
            }}
          >
            View All Services
          </ViewAllButton>
        </ButtonContainer>
      </Container>
    </SectionContainer>
  );
};

// Keyframes
const cardGlow = keyframes`
  0%, 100% {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(255, 215, 0, 0.1);
  }
`;

// Styled Components
const SectionContainer = styled.section`
  background-color: var(--darker-bg);
  position: relative;
  overflow: hidden;
  padding: var(--spacing-xl) 0;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  height: 800px;
  background: radial-gradient(
    circle at center,
    rgba(255, 215, 0, 0.03) 0%,
    transparent 70%
  );
  z-index: 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-lg);

  .subtitle {
    color: var(--gold);
    text-transform: uppercase;
    letter-spacing: 4px;
    font-size: 0.9rem;
    margin-bottom: var(--spacing-xs);
    display: block;
  }

  h2 {
    font-size: 3rem;
    background: var(--gold-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: var(--spacing-sm);
    position: relative;
    display: inline-block;
    text-transform: uppercase;
    font-weight: 800;

    &::after {
      content: "";
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: var(--gold-gradient);
      border-radius: 2px;
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }

    @media (max-width: 576px) {
      font-size: 2rem;
    }
  }

  p {
    max-width: 600px;
    margin: var(--spacing-md) auto 0;
    font-size: 1.1rem;
    color: #d0d0d0;
    line-height: 1.8;

    @media (max-width: 576px) {
      font-size: 1rem;
    }
  }
`;

const ServicesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-md);

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 215, 0, 0.1);
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  text-align: center;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    background: rgba(255, 215, 0, 0.05);
    border-color: rgba(255, 215, 0, 0.3);
    transform: translateY(-8px);
    animation: ${cardGlow} 2s ease-in-out infinite;
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  background: var(--gold-gradient);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-md);
  transition: all 0.3s ease;

  svg {
    font-size: 1.8rem;
    color: var(--black);
  }

  ${ServiceCard}:hover & {
    transform: scale(1.1) rotate(-5deg);
    box-shadow: 0 10px 25px rgba(255, 215, 0, 0.4);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.3rem;
  color: white;
  margin-bottom: var(--spacing-sm);
  font-weight: 600;
`;

const ServiceDescription = styled.p`
  font-size: 0.95rem;
  color: #b0b0b0;
  line-height: 1.6;
  margin-bottom: var(--spacing-sm);
`;

const ButtonContainer = styled(motion.div)`
  text-align: center;
  margin-top: var(--spacing-lg);
`;

const ViewAllButton = styled(motion(Link))`
  display: inline-block;
  padding: 16px 40px;
  background: transparent;
  color: var(--gold);
  border: 1px solid var(--gold);
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: var(--gold-gradient);
    color: var(--black);
    border-color: transparent;
  }

  @media (max-width: 576px) {
    padding: 14px 30px;
    font-size: 0.9rem;
    width: 100%;
  }
`;

export default ServicesSection;
